import { IntegrationType } from '@/shared/types/integration';
import {
  WebhookType,
  type WebhookPayload,
  type WebhookTestParameters,
  type WebhookTestSchema,
} from '@/shared/types/webhook';
import { getTestWebhookValues } from '@/util/constants';

export interface ParsedJson {
  isValid: boolean;
  payload: WebhookPayload | null;
}

export const parseJsonPayload = (jsonString: string): ParsedJson => {
  if (!jsonString || jsonString.trim().length === 0) {
    return { isValid: false, payload: null };
  }

  try {
    const parsed = JSON.parse(jsonString);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { isValid: false, payload: null };
    }
    return { isValid: true, payload: parsed as WebhookPayload };
  } catch {
    return { isValid: false, payload: null };
  }
};

/** Transforms a webhook body payload (editor format) into test_parameters (orchestrator format). */
export const toTestParameters = (payload: WebhookPayload): WebhookTestParameters => ({
  business_closed: payload.business_closed ?? false,
  lang: payload.lang ?? 'en',
  channel: payload.channel ?? IntegrationType.WEB,
  context: (payload.context ?? {}) as WebhookTestParameters['context'],
  trigger: {
    ...(payload.input?.text != null ? { text: payload.input.text } : {}),
    intents: payload.intents ?? [],
    entities: payload.entities ?? [],
  },
});

/** Safely parses `webhook.test_schema` (string | object | undefined) into a typed object. */
export const parseTestSchema = (testSchema: unknown): WebhookTestSchema => {
  if (!testSchema) {
    return {};
  }
  if (typeof testSchema === 'string') {
    try {
      return JSON.parse(testSchema) as WebhookTestSchema;
    } catch {
      return {};
    }
  }
  if (typeof testSchema === 'object') {
    return testSchema as WebhookTestSchema;
  }
  return {};
};

/** Returns the default test payload for a webhook type as a formatted JSON string. */
export const formatTestPayload = (type: WebhookType = WebhookType.ACTION): string =>
  JSON.stringify(getTestWebhookValues(type), null, 2);

export const sanitizeHeaders = (headers: Array<{ name?: string; value?: string }> = []) => {
  return headers
    .map((header) => ({ name: header?.name?.trim() ?? '', value: header?.value ?? '' }))
    .filter((header) => header.name.length > 0);
};
